// One place that decides how a rupee figure reaches the screen.
//
// The *permission* is the server's: money fields are stripped from API responses
// for logins that may not see them, so by the time a value arrives here it is
// either real or already `null`. This module exists for the two things the server
// cannot do — draw a lock where a number was, and let somebody who is allowed to
// see money hide it anyway while their screen is on a projector.
//
// The visibility flag is module-level rather than a hook argument so that plain
// formatter functions, template literals and table cells can consult it without
// every one of them becoming a component.

export const MONEY_MASK = '•••••';

let visible = true;
const listeners = new Set();

export function setMoneyVisible(next) {
  const value = Boolean(next);
  if (value === visible) return;
  visible = value;
  listeners.forEach((fn) => {
    try { fn(value); } catch { /* a bad listener must not stop the others */ }
  });
}

export function isMoneyVisible() {
  return visible;
}

export function subscribeMoney(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Format one monetary figure.
 *
 * Three states, and they are deliberately different on screen:
 *  - hidden     → the mask, because the viewer is not allowed to know
 *  - null/undef → a dash, because nobody recorded it
 *  - a number   → the figure
 *
 * Collapsing "hidden" into "not recorded" is the mistake this avoids: a buyer
 * reading a blank cost column would conclude the plant does not know its costs.
 */
export function formatMoney(value, options = {}) {
  const {
    currency = '₹',
    decimals = 2,
    blank = '—',
    zeroAsBlank = false,
    compact = false,
  } = options;
  if (!visible) return MONEY_MASK;
  if (value === null || value === undefined || value === '') return blank;
  const n = Number(value);
  if (!Number.isFinite(n)) return blank;
  if (zeroAsBlank && n === 0) return blank;
  if (compact && Math.abs(n) >= 100000) {
    return `${currency}${(n / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} L`;
  }
  return `${currency}${n.toLocaleString('en-IN', { maximumFractionDigits: decimals })}`;
}

/** Per-kg rates, which are written with the unit far more often than not. */
export function formatRate(value, options = {}) {
  const formatted = formatMoney(value, { decimals: 2, ...options });
  if (formatted === MONEY_MASK || formatted === (options.blank ?? '—')) return formatted;
  return `${formatted}/kg`;
}

/** For the rare place that needs the raw number and not a string. */
export function maskedNumber(value) {
  return visible ? value : null;
}

/**
 * Wrap a rendered money string.
 *
 * Takes a *thunk*, not a string, and that is the whole point: when money is
 * hidden the expression is never evaluated. A login that may not see prices gets
 * `null` from the server for every one of them, and `null.toFixed(2)` throws — so
 * a wrapper that formatted first and masked afterwards would turn a permission
 * into a white screen. Not evaluating is both the mask and the null-safety.
 *
 * The catch is the second half of the same argument: an elite user with a null
 * figure sees a dash rather than a crashed page.
 */
export function maskMoney(render, blank = '\u2014') {
  if (!visible) return MONEY_MASK;
  try {
    const out = typeof render === 'function' ? render() : render;
    return out === null || out === undefined ? blank : out;
  } catch {
    return blank;
  }
}
